import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { CreditCard, Loader2, Layers, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  ACTIVE: { label: "Active", cls: "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30" },
  PAST_DUE: { label: "Past due", cls: "bg-red-500/15 text-red-400 border border-red-500/30" },
  GRACE_PERIOD: { label: "Grace period", cls: "bg-amber-500/15 text-amber-400 border border-amber-500/30" },
  PENDING_PAYMENT_METHOD: { label: "Add a card", cls: "bg-sky-500/15 text-sky-300 border border-sky-500/30" },
  TERMINATED: { label: "Terminated", cls: "bg-muted text-muted-foreground border border-border" },
};

export default function ShopBillingPanel() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/shop/billing/summary"); setSummary(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    if (params.get("card") === "saved" && sid) {
      (async () => {
        try {
          const { data } = await api.get(`/shop/billing/setup-status/${sid}`);
          if (data.card_saved) toast.success("Payment method saved — your leases are now billable.");
        } catch { /* ignore */ }
        window.history.replaceState({}, "", "/shop?tab=billing");
        load();
      })();
    } else {
      load();
    }
  }, []);

  const addCard = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/shop/billing/setup-checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); setBusy(false); }
  };

  const manageCard = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/shop/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); setBusy(false); }
  };

  const cancelLease = async (zoneId) => {
    try { const { data } = await api.post(`/shop/leases/${zoneId}/cancel`); toast.success(data.effectiveDate ? `Lease will end ${String(data.effectiveDate).slice(0, 10)} — you keep the zone and payouts until then.` : "Lease cancelled."); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const resumeLease = async (zoneId) => {
    try { await api.post(`/shop/leases/${zoneId}/resume`); toast.success("Cancellation reverted — your lease will renew."); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (loading) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  if (!summary) return null;

  const card = summary.card || {};
  const zones = (summary.zones || []).filter((z) => z.billingStatus !== "TERMINATED");

  return (
    <div className="space-y-6" data-testid="shop-billing-panel">
      <div>
        <h2 className="font-heading text-lg font-bold">Lease billing</h2>
        <p className="text-sm text-muted-foreground">Your leased zones, renewal dates, and payment method on file.</p>
      </div>

      {/* Card on file */}
      <div className="rounded-2xl border border-border bg-card p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="shop-card-on-file">
        <div className="flex items-center gap-3 flex-1">
          <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center"><CreditCard className="w-5 h-5 text-primary" /></div>
          <div>
            <div className="font-medium">Payment method</div>
            {card.saved ? (
              <div className="text-sm text-muted-foreground" data-testid="shop-card-summary">{(card.brand || "Card").toUpperCase()} •••• {card.last4 || "----"}</div>
            ) : (
              <div className="text-sm text-amber-400">No card on file — add one to activate lease billing.</div>
            )}
          </div>
        </div>
        {card.saved
          ? <Button variant="outline" onClick={manageCard} disabled={busy} data-testid="shop-manage-card-btn">{busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Update payment method</Button>
          : <Button onClick={addCard} disabled={busy} data-testid="shop-add-card-btn">{busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Add payment method</Button>}
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 gap-4">
        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Leased zones</div>
          <div className="text-2xl font-heading font-bold mt-1" data-testid="shop-lease-count">{summary.count || 0}</div>
        </div>
        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Total annual lease fees</div>
          <div className="text-2xl font-heading font-bold mt-1" data-testid="shop-lease-total">${(summary.totalAnnual || 0).toFixed(2)}</div>
        </div>
      </div>

      {/* Leased zones */}
      {zones.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-12 text-center" data-testid="shop-billing-empty">
          <Layers className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <div className="font-heading font-bold">No leased zones yet</div>
          <p className="text-sm text-muted-foreground mt-1">Velopaths will lease zones to your shop. Lease fees appear here once assigned.</p>
        </div>
      ) : (
        <div className="rounded-2xl border border-border bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Zone</TableHead>
                <TableHead>Annual fee</TableHead>
                <TableHead>Renewal</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {zones.map((z) => {
                const s = STATUS[z.billingStatus] || STATUS.ACTIVE;
                return (
                  <TableRow key={z.zoneId} data-testid={`shop-lease-row-${z.zoneId}`}>
                    <TableCell>
                      <div className="font-medium">{z.zoneName || "Zone"}</div>
                      {z.humanId && <div className="text-xs text-muted-foreground">{z.humanId}</div>}
                    </TableCell>
                    <TableCell>${Number(z.annualFee).toFixed(2)}/yr</TableCell>
                    <TableCell>{z.cancelScheduled ? <span className="text-amber-400">Ends {z.cancelEffectiveDate || "—"}</span> : (z.renewalDate || <span className="text-muted-foreground">—</span>)}</TableCell>
                    <TableCell>{z.cancelScheduled ? <Badge className="bg-amber-500/15 text-amber-400 border border-amber-500/30" data-testid={`shop-lease-status-${z.zoneId}`}>Cancels {z.cancelEffectiveDate || ""}</Badge> : <Badge className={s.cls} data-testid={`shop-lease-status-${z.zoneId}`}>{s.label}</Badge>}</TableCell>
                    <TableCell className="text-right">
                      {z.cancelScheduled ? (
                        <Button size="sm" variant="outline" onClick={() => resumeLease(z.zoneId)} data-testid={`shop-resume-lease-${z.zoneId}`}>Resume lease</Button>
                      ) : (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300" data-testid={`shop-cancel-lease-${z.zoneId}`}>Cancel lease</Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Cancel this lease?</AlertDialogTitle>
                            <AlertDialogDescription>
                              {z.zoneName} won’t renew. You keep the zone and continue receiving advertiser payouts until {z.renewalDate || "the end of your paid term"}, then it returns to Velopaths. Billing stops after that (no refund for the current term). You can resume anytime before then.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Keep lease</AlertDialogCancel>
                            <AlertDialogAction onClick={() => cancelLease(z.zoneId)} data-testid={`shop-cancel-lease-confirm-${z.zoneId}`}>Cancel at period end</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <p className="text-xs text-muted-foreground flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Cards are stored securely by Stripe. Velopaths never sees your full card number.</p>
    </div>
  );
}
